import { useState, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Image as ImageIcon, UploadSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { resizeImage } from '../lib/imageResize';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (url) => url ? (url.startsWith('http') ? url : `${API_BASE}${url}`) : null;

const SLOTS = [
  { key: 'logo', label: 'Logo', maxSide: 800, maxMB: 5, forceFormat: null, quality: 0.85 },
  { key: 'cover', label: 'Cover Photo', maxSide: 1600, maxMB: 10, forceFormat: 'image/jpeg', quality: 0.8 },
];

const OPTIONS = [
  { value: 'default', label: 'Use account default' },
  { value: 'upload', label: 'Upload for this event' },
  { value: 'none', label: 'None' },
];

function currentOption(tripBranding, slot) {
  const val = (tripBranding || {})[slot];
  if (!val || val === 'default') return 'default';
  if (val === 'none') return 'none';
  return 'upload'; // has a URL
}

export default function EventBrandingCard({ trip, code, onUpdate }) {
  const tripBranding = trip?.branding || {};
  const fileRefs = { logo: useRef(null), cover: useRef(null) };
  const [uploading, setUploading] = useState({});

  const resolvedLogo = resolveUrl(tripBranding.logo_url);
  const resolvedCover = resolveUrl(tripBranding.cover_url);

  const handleOptionChange = async (slot, option) => {
    if (option === 'default') {
      try {
        await api.patch(`/trips/${code}/branding`, { [slot]: 'default' });
        const { data } = await api.get(`/trips/${code}`);
        onUpdate(data);
        toast.success(`${slot === 'logo' ? 'Logo' : 'Cover'} reset to account default`);
      } catch { toast.error('Failed to update'); }
    } else if (option === 'none') {
      try {
        await api.delete(`/trips/${code}/branding/${slot}`);
        const { data } = await api.get(`/trips/${code}`);
        onUpdate(data);
        toast.success(`${slot === 'logo' ? 'Logo' : 'Cover'} set to none`);
      } catch { toast.error('Failed to update'); }
    } else if (option === 'upload') {
      fileRefs[slot].current?.click();
    }
  };

  const handleUpload = async (slot, file) => {
    const cfg = SLOTS.find(s => s.key === slot);
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Use PNG, JPEG, or WebP.'); return; }
    if (file.size > cfg.maxMB * 1024 * 1024) { toast.error(`Max ${cfg.maxMB}MB.`); return; }

    setUploading(prev => ({ ...prev, [slot]: true }));
    try {
      const opts = { maxSide: cfg.maxSide, quality: cfg.quality };
      if (cfg.forceFormat) opts.forceFormat = cfg.forceFormat;
      const { blob, mimeType } = await resizeImage(file, opts);
      const ext = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg';
      const formData = new FormData();
      formData.append('file', blob, `${slot}.${ext}`);
      await api.post(`/trips/${code}/branding/${slot}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await api.get(`/trips/${code}`);
      onUpdate(data);
      toast.success(`${slot === 'logo' ? 'Logo' : 'Cover photo'} uploaded`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally { setUploading(prev => ({ ...prev, [slot]: false })); }
  };

  return (
    <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-4" data-testid="event-branding-card">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-5 h-5 text-[#1B4332]" weight="bold" />
        <h2 className="font-bold text-[#1D242B] text-base">Event Branding</h2>
      </div>

      {SLOTS.map(slot => {
        const opt = currentOption(tripBranding, slot.key);
        const resolved = slot.key === 'logo' ? resolvedLogo : resolvedCover;
        const isUploading = uploading[slot.key];

        return (
          <div key={slot.key} className="space-y-2" data-testid={`event-branding-${slot.key}`}>
            <p className="text-xs font-semibold text-[#455561]">{slot.label}</p>

            {/* Radio options */}
            <div className="flex flex-wrap gap-2">
              {OPTIONS.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => o.value !== 'upload' ? handleOptionChange(slot.key, o.value) : fileRefs[slot.key].current?.click()}
                  disabled={isUploading}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                    opt === o.value
                      ? 'border-[#1B4332] bg-[#1B4332] text-white'
                      : 'border-[#E1E6E4] text-[#455561] hover:border-[#1B4332]/40'
                  }`}
                  data-testid={`event-branding-${slot.key}-${o.value}`}
                >
                  {o.value === 'upload' && isUploading ? 'Uploading...' : o.label}
                </button>
              ))}
            </div>

            {/* Preview — shows resolved image (what the player sees) */}
            {resolved && opt !== 'none' && (
              <div className={`rounded-lg overflow-hidden bg-[#F4F6F5] ${slot.key === 'cover' ? 'h-24' : 'h-16 flex items-center justify-center'}`}>
                <img
                  src={`${resolved}${resolved.includes('?') ? '&' : '?'}t=${Date.now()}`}
                  alt={slot.label}
                  className={slot.key === 'cover' ? 'w-full h-full object-cover object-center' : 'max-h-[56px] w-auto object-contain'}
                  data-testid={`event-branding-${slot.key}-preview`}
                />
              </div>
            )}
            {opt === 'none' && slot.key === 'logo' && (
              <p className="text-[10px] text-[#8A9BA3] italic">No logo will be shown for this event.</p>
            )}
            {opt === 'none' && slot.key === 'cover' && (
              <p className="text-[10px] text-[#8A9BA3] italic">Stock cover photo will be used.</p>
            )}

            <input
              ref={fileRefs[slot.key]}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid={`event-branding-${slot.key}-file`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) handleUpload(slot.key, f);
              }}
            />
          </div>
        );
      })}
    </Card>
  );
}
